// Builds the ⌘K search index from the same data the terminal prints,
// so the palette can never drift from the commands.

import { commands } from '../data/commands';
import { caseStudies } from '../data/caseStudies';

const COMMAND_BLURBS = {
  help: 'All available commands',
  about: 'Who I am and what I work on',
  skills: 'Languages, AI engineering, backend, cloud',
  experience: 'Roles at Agito Global, AB InBev, Perfios',
  contact: 'Email, LinkedIn, GitHub, phone',
  education: 'Dayananda Sagar College of Engineering',
  certifications: 'Awards and certifications',
  resume: 'Download the PDF',
  projects: 'Public repositories, live from GitHub',
  theme: 'Switch colour scheme',
  clear: 'Clear the terminal'
};

// A resume line is worth surfacing if it carries substance — skip
// headers, blank spacers and decoration.
const isSubstantive = (line) => {
  const t = line.trim();
  if (t.length < 24) return false;
  if (/^[=—-]+$/.test(t)) return false;
  return /^[•›]/.test(t) || /[a-z]{4}/.test(t);
};

const stripLeading = (line) => line.trim().replace(/^[•›]\s*/, '');

export function buildIndex() {
  const entries = [];

  Object.keys(commands).forEach((name) => {
    entries.push({
      kind: 'command',
      id: `cmd:${name}`,
      title: name,
      subtitle: COMMAND_BLURBS[name] || '',
      run: name
    });
  });

  entries.push({
    kind: 'command',
    id: 'cmd:case-study',
    title: 'case-study',
    subtitle: 'Deep dive on one piece of work',
    run: 'case-study'
  });

  // Resume body text, attributed back to the command that prints it.
  Object.entries(commands).forEach(([name, lines]) => {
    if (name === 'help') return;
    lines.filter(isSubstantive).forEach((line, i) => {
      entries.push({
        kind: 'resume',
        id: `res:${name}:${i}`,
        title: name,
        subtitle: stripLeading(line),
        run: name
      });
    });
  });

  Object.values(caseStudies).forEach((cs) => {
    entries.push({
      kind: 'case-study',
      id: `cs:${cs.slug}`,
      title: `case-study ${cs.slug}`,
      subtitle: cs.title,
      run: `case-study ${cs.slug}`
    });
    cs.points.forEach((p, i) => {
      entries.push({
        kind: 'case-study',
        id: `cs:${cs.slug}:${i}`,
        title: cs.slug,
        subtitle: p,
        run: `case-study ${cs.slug}`
      });
    });
  });

  entries.push({
    kind: 'action',
    id: 'act:resume',
    title: 'Download resume',
    subtitle: 'pdf',
    run: 'resume'
  });

  return entries;
}

// Subsequence match, the way command palettes behave: "azfn" finds
// "Azure Functions". Scores earlier and tighter matches higher.
function fuzzy(query, text) {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (!q) return { hit: true, score: 0, ranges: [] };

  const direct = t.indexOf(q);
  if (direct !== -1) {
    return {
      hit: true,
      score: 1000 - direct - Math.abs(t.length - q.length) * 0.1,
      ranges: [[direct, direct + q.length]]
    };
  }

  let ti = 0;
  const ranges = [];
  for (let qi = 0; qi < q.length; qi += 1) {
    const found = t.indexOf(q[qi], ti);
    if (found === -1) return { hit: false, score: 0, ranges: [] };
    ranges.push([found, found + 1]);
    ti = found + 1;
  }
  const spread = ranges[ranges.length - 1][1] - ranges[0][0];
  return { hit: true, score: 400 - spread - ranges[0][0] * 0.5, ranges };
}

const KIND_WEIGHT = { command: 60, action: 40, 'case-study': 20, resume: 0 };

export function search(entries, query, limit = 12) {
  if (!query.trim()) {
    return entries.filter((e) => e.kind === 'command').slice(0, limit)
      .map((e) => ({ entry: e, ranges: [], field: 'title' }));
  }

  const scored = [];
  entries.forEach((entry) => {
    const onTitle = fuzzy(query, entry.title);
    const onSub = fuzzy(query, entry.subtitle || '');
    if (!onTitle.hit && !onSub.hit) return;

    const titleScore = onTitle.hit ? onTitle.score + 200 : -Infinity;
    const subScore = onSub.hit ? onSub.score : -Infinity;
    const useTitle = titleScore >= subScore;

    scored.push({
      entry,
      field: useTitle ? 'title' : 'subtitle',
      ranges: useTitle ? onTitle.ranges : onSub.ranges,
      score: Math.max(titleScore, subScore) + (KIND_WEIGHT[entry.kind] || 0)
    });
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

// Splits a string into [text, isMatch] segments for highlighting.
export function highlight(text, ranges) {
  if (!ranges || !ranges.length) return [[text, false]];
  const out = [];
  let cursor = 0;
  ranges.forEach(([start, end]) => {
    if (start > cursor) out.push([text.slice(cursor, start), false]);
    out.push([text.slice(start, end), true]);
    cursor = end;
  });
  if (cursor < text.length) out.push([text.slice(cursor), false]);
  return out;
}
